import { Arrow, Circle, Group, Line, Rect, Text } from "react-konva";
import { chromeScale } from "../../lib/canvasLabels";
import {
  FENG_SHUI_DEMO_GEOMETRY,
  useFengShuiDemo,
  type FengShuiDemoCallout,
} from "../../lib/fengShuiDemo";

/** Same reds / greens / teal as the Reel's callouts. */
const ISSUE = "#FF3B30";
const FIX = "#34C759";
const CHI = "#30B0C7";

interface Props {
  /** Room inches → stage px (the canvas's fit). */
  pointToPx: (p: { x: number; y: number }) => { x: number; y: number };
  stageScale: number;
}

/**
 * The feng shui landing's overlays, drawn inside the zoomed Stage so they
 * stay pinned to the pieces: the coffin arrow and red issue callouts on the
 * "before" room, the qi path and green fix callouts as the rearrangement
 * lands. Chrome-sized (constant on screen at every zoom). Never listens.
 */
export function FengShuiDemoLayer({ pointToPx, stageScale }: Props) {
  const demo = useFengShuiDemo();
  if (demo.phase === "idle") return null;
  const k = chromeScale(stageScale);
  const { coffinLine, chiPoints } = FENG_SHUI_DEMO_GEOMETRY;
  return (
    <Group listening={false} data-testid="feng-shui-demo-layer">
      {demo.coffinLine &&
        (() => {
          const from = pointToPx(coffinLine.from);
          const to = pointToPx(coffinLine.to);
          return (
            <Arrow
              points={[from.x, from.y, to.x, to.y]}
              stroke={ISSUE}
              fill={ISSUE}
              strokeWidth={3 * k}
              pointerLength={10 * k}
              pointerWidth={10 * k}
              lineCap="round"
              opacity={0.95}
            />
          );
        })()}
      {demo.chi &&
        (() => {
          const pts = chiPoints.map(pointToPx);
          return (
            <>
              <Line
                points={pts.flatMap((p) => [p.x, p.y])}
                stroke={CHI}
                strokeWidth={3 * k}
                dash={[1 * k, 8 * k]}
                lineCap="round"
                tension={0.5}
                opacity={0.9}
              />
              <Circle
                x={pts[0].x}
                y={pts[0].y}
                radius={4 * k}
                fill={CHI}
                opacity={0.9}
              />
            </>
          );
        })()}
      {demo.callouts.map((c) => (
        <Callout key={c.key} callout={c} at={pointToPx(c.at)} scale={k} />
      ))}
    </Group>
  );
}

function Callout({
  callout,
  at,
  scale,
}: {
  callout: FengShuiDemoCallout;
  at: { x: number; y: number };
  scale: number;
}) {
  const issue = callout.tone === "issue";
  const text = `${issue ? "✕" : "✓"}  ${callout.text}`;
  // Width by character count, the same estimate the collaborator pills use.
  const fontSize = 12;
  const charW = 6.9;
  const padX = 10;
  const w = Math.round(text.length * charW + padX * 2);
  const h = 26;
  return (
    <Group x={at.x} y={at.y} scaleX={scale} scaleY={scale} listening={false}>
      <Rect
        x={-w / 2}
        y={-h / 2}
        width={w}
        height={h}
        cornerRadius={h / 2}
        fill={issue ? ISSUE : FIX}
        stroke="#ffffff"
        strokeWidth={2}
        shadowColor="#000"
        shadowBlur={8}
        shadowOpacity={0.22}
        shadowOffsetY={2}
      />
      <Text
        x={-w / 2}
        y={-h / 2}
        width={w}
        height={h}
        text={text}
        align="center"
        verticalAlign="middle"
        fontSize={fontSize}
        fontStyle="700"
        fontFamily="Inter, system-ui, sans-serif"
        fill="#ffffff"
      />
    </Group>
  );
}
